import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import Input from "../../../../components/Form/Input";
import { useAppState } from "../../../../context/StateContext";
import useYupValidation from "../../../../hooks/useYupValidation";
import {
  MAX_CHARACTERS_PASSWORD,
  MIN_CHARACTERS_PASSWORD,
  RGX_SPECIAL_CHARACTERS,
} from "../../UpdatePassword/utils";
import useStyles, {
  HidePasswordIcon,
  LoginForm,
  LoginFormWrapper,
  LoginInfo,
  LoginSubtitle,
  LoginTitle,
  LogoImage,
  ResetPasswordButton,
  ShowPasswordIcon,
} from "./styles";

interface UpdatePasswordFormProps {
  handleSubmit?: (event: React.FormEvent) => void;
  isLoading?: boolean;
}

export interface UpdatePasswordFormHandler {
  newPassword: string;
  confirmPassword: string;
}

const UpdatePasswordForm: ForwardRefRenderFunction<
  UpdatePasswordFormHandler,
  UpdatePasswordFormProps
> = (props, ref) => {
  const { handleSubmit, isLoading } = props;

  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { subDomainInfo } = useAppState();
  const { t: translate } = useTranslation();

  const classes = useStyles();

  const validateForm = () => {
    try {
      const schema = yup.object().shape({
        newPassword: yup
          .string()
          .typeError(translate("pleaseEnterAPassword"))
          .required(translate("pleaseEnterAPassword"))
          .test(
            "min-len",
            translate("yourPasswordMustBeAtLeast12CharactersLong"),
            (newPassword) => !!newPassword && newPassword.length >= MIN_CHARACTERS_PASSWORD
          )
          .test(
            "max-len",
            translate("yourPasswordMustBeAtMaximumOf30CharactersLong"),
            (newPassword) => !!newPassword && newPassword.length < MAX_CHARACTERS_PASSWORD
          )
          .test(
            "min-special-len",
            translate("yourPasswordMustContainAtLeastOneSpecialCharacter"),
            (newPassword) => !!newPassword && !!newPassword.match(RGX_SPECIAL_CHARACTERS)
          ),
        confirmPassword: yup
          .string()
          .typeError(translate("newPasswordAndConfirmPasswordMustBeTheSame"))
          .oneOf(
            [yup.ref("newPassword"), null],
            translate("newPasswordAndConfirmPasswordMustBeTheSame")
          ),
      });

      schema.validateSync(
        {
          newPassword,
          confirmPassword,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  const handleSubmitForm = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitted(true);

    if (validateForm()) {
      if (handleSubmit) handleSubmit(event);
    }
  };

  useEffect(() => {
    if (isSubmitted) validateForm();
  }, [isSubmitted, newPassword, confirmPassword]);

  useImperativeHandle(ref, () => ({
    newPassword,
    confirmPassword,
  }));

  return (
    <LoginForm onSubmit={handleSubmitForm}>
      <LogoImage src={subDomainInfo?.logo} />

      <LoginInfo>
        <LoginTitle>{translate("newPassword")}</LoginTitle>
        <LoginSubtitle>{translate("pleaseCreateANewPasswordForYourAccount")}</LoginSubtitle>
      </LoginInfo>

      <LoginFormWrapper>
        <Input
          fullWidth
          inputProps={{
            "aria-label": "user-pwd",
            autoComplete: "new-password",
          }}
          type={showNewPassword ? "text" : "password"}
          label={translate("newPassword")}
          labelClassName={classes.label}
          rightIcon={
            showNewPassword ? (
              <ShowPasswordIcon size={22} onClick={() => setShowNewPassword(false)} />
            ) : (
              <HidePasswordIcon size={22} onClick={() => setShowNewPassword(true)} />
            )
          }
          value={newPassword}
          disabled={isLoading}
          placeholder={translate("typeHere")}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setNewPassword(event.target.value)
          }
          error={fieldHasError("newPassword")}
          errorMessage={getErrorByField("newPassword")}
        />
        <Input
          fullWidth
          inputProps={{
            "aria-label": "user-pwd",
            autoComplete: "new-password",
          }}
          type={showConfirmPassword ? "text" : "password"}
          label={translate("confirmPassword")}
          labelClassName={classes.label}
          rightIcon={
            showConfirmPassword ? (
              <ShowPasswordIcon size={22} onClick={() => setShowConfirmPassword(false)} />
            ) : (
              <HidePasswordIcon size={22} onClick={() => setShowConfirmPassword(true)} />
            )
          }
          value={confirmPassword}
          disabled={isLoading}
          placeholder={translate("typeHere")}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setConfirmPassword(event.target.value)
          }
          error={fieldHasError("confirmPassword")}
          errorMessage={getErrorByField("confirmPassword")}
        />
      </LoginFormWrapper>

      <ResetPasswordButton
        type="submit"
        fullWidth
        loading={isLoading}
        data-cy="createNewPasswordButton"
        aria-label="createNewPasswordButton"
      >
        {translate("createNewPassword")}
      </ResetPasswordButton>
    </LoginForm>
  );
};

export default forwardRef(UpdatePasswordForm);
